import React, { useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LuBookOpen, LuCoins, LuGamepad2, LuInfo, LuLogIn, LuMenu, LuPalette, LuX } from 'react-icons/lu'
import { useMediaQuery } from '@mui/system'
import { Theme } from '@mui/material'
import { Button } from './components/button'
import DotAdsLanding from './DotAdsLanding'
import LiveChatPrompt from './LiveChatPrompt'
import { PROMPT_PARENT_ID } from '../../services/googleOneTapLogin/config'

// Declare global for Calendly TypeScript support
declare global {
  interface Window {
    Calendly: any
  }
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  // 1. Optimized Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 2. Safe Script Loading (GTM & Calendly)
  useEffect(() => {
    // GTM (Only if not already present)
    if (!document.getElementById('gtm-script')) {
      const gtmScript = document.createElement('script')
      gtmScript.id = 'gtm-script'
      gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-PSRZJKJ');`
      document.head.appendChild(gtmScript)
    }

    // Calendly
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement('script')
      script.src = 'https://assets.calendly.com/assets/external/widget.js'
      script.async = true
      document.head.appendChild(script)

      const link = document.createElement('link')
      link.href = 'https://assets.calendly.com/assets/external/widget.css'
      link.rel = 'stylesheet'
      document.head.appendChild(link)

      const style = document.createElement('style')
      style.textContent = `
        .calendly-overlay {
          backdrop-filter: blur(4px) !important;
          z-index: 9999 !important;
          animation: fadeIn 0.3s ease-in-out !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .calendly-popup-content {
          position: relative !important;
          border-radius: 20px !important;
          overflow: hidden !important;
          max-width: 1200px !important;
          width: 100% !important;
          max-height: calc(100vh - 40px) !important;
          margin: 20px auto !important;
          animation: slideUp 0.4s ease-out !important;
          background: white !important;
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .calendly-popup-close {
          visibility: hidden;
        }

        .calendly-popup .calendly-inline-widget {
          border-radius: 20px !important;
          background: white !important;
          min-height: 600px !important;
          height: 100% !important;
        }
        .calendly-popup .calendly-inline-widget iframe {
          border-radius: 20px !important;
        }

        @media (max-width: 768px) {
          .calendly-popup-content {
            margin: 10px auto !important;
            border-radius: 16px !important;
            width: calc(100vw - 20px) !important;
            max-height: calc(100vh - 20px) !important;
          }

          .calendly-popup .calendly-inline-widget {
            border-radius: 16px !important;
            min-height: 500px !important;
          }

          .calendly-popup .calendly-inline-widget iframe {
            border-radius: 16px !important;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .calendly-popup-content {
            max-width: 90vw !important;
            margin: 20px auto !important;
          }
        }

        .calendly-overlay * {
          transition: all 0.2s ease !important;
        }

        .calendly-badge-widget {
          z-index: 9998 !important;
        }

        .calendly-popup {
          z-index: 9999 !important;
        }

        body.calendly-popup-open {
          overflow: hidden !important;
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  const openCalendly = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/d/dxg-58r-rm4/introduction-to-dot-discovery-call?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=dd4caa'
      })
    } else {
      window.open('https://calendly.com/d/dxg-58r-rm4/introduction-to-dot-discovery-call', '_blank')
    }
  }, [])

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          title="GTM"
          src="https://www.googletagmanager.com/ns.html?id=GTM-PSRZJKJ"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        ></iframe>
      </noscript>
      {/* End Google Tag Manager (noscript) */}

      <style>{`
        .glow-text {
          text-shadow: 0 0 30px rgba(155, 92, 255, 0.5);
        }
        .glow-button {
          box-shadow: 0 0 30px rgba(155, 92, 255, 0.3);
        }
        .glow-button:hover {
          box-shadow: 0 0 40px rgba(155, 92, 255, 0.5);
        }

        /* Desktop-only scaling for LiveChatPrompt */
        @media (min-width: 1024px) {
          .chat-modal-scale {
            font-size: 1.25em; /* 25% larger content */
          }
          .chat-modal-scale > div {
            transform: scale(1.5); /* 50% larger modal */
            transform-origin: bottom right;
          }
        }
      `}</style>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <a
              href={'https://dotaudiences.com'}
              onClick={() => window.scrollTo(0, 0)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center group"
            >
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/690a28bada88e702ec8f793d/572564efd_DOTLogo-Basic-White.png"
                alt="DOT Logo"
                className="h-20 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href={'https://dotaudiences.com/cryptoproducts'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
              >
                <LuCoins className="w-3.5 h-3.5" />
                Crypto Solutions
              </a>
              <a
                href={'https://dotaudiences.com/igamingproducts'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
              >
                <LuGamepad2 className="w-3.5 h-3.5" />
                iGaming Solutions
              </a>
              <a
                href={'https://dotaudiences.com/creativevault'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
              >
                <LuPalette className="w-3.5 h-3.5" />
                Creative Vault
              </a>
              <a
                href={'https://dotaudiences.com/support'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
              >
                <LuBookOpen className="w-3.5 h-3.5" />
                Docs
              </a>
              <a
                href={'https://dotaudiences.com/aboutus'}
                onClick={() => window.scrollTo(0, 0)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
              >
                <LuInfo className="w-3.5 h-3.5" />
                About Us
              </a>
              <div className="h-6 w-px bg-white/10 mx-8"></div>
              <div className="relative flex items-center gap-3">
                <div className="flex items-center gap-3">
                  <Link
                    to={'/login'}
                    className="text-[#BFBFBF] hover:text-white transition-colors font-medium text-sm flex items-center gap-1"
                  >
                    <LuLogIn className="w-3.5 h-3.5" />
                    Login
                  </Link>

                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-white hover:bg-slate-200 text-[#161616] transition-colors font-medium text-sm flex items-center gap-1"
                  >
                    <LuLogIn className="w-3.5 h-3.5" />
                    Register
                  </Button>

                  <Button
                    onClick={openCalendly}
                    className="bg-gradient-to-r from-[#9B5CFF] to-[#EC4899] hover:from-[#8B4CFF] hover:to-[#DB2777] text-white glow-button px-6 py-2.5"
                  >
                    Book a Demo
                  </Button>
                </div>
                <div id={PROMPT_PARENT_ID} className="absolute top-full right-0 mt-2 z-[100]" />
              </div>
            </div>

            {isMobile && (
              <div className="lg:hidden flex items-center gap-3">
                <a
                  href="https://t.me/DotAds_Bot?start=64d0f2cf2f590d0625058864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all"
                >
                  <div className="relative">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#229ED9">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.155.23.171.324.016.094.036.308.02.475z" />
                    </svg>
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
                  </div>
                  <span className="text-sm font-medium text-white">Live Chat</span>
                </a>

                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-white">
                  {mobileMenuOpen ? <LuX className="w-6 h-6" /> : <LuMenu className="w-6 h-6" />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#1A1A1A] backdrop-blur-xl border-t border-white/10">
            <div className="px-4 py-6 space-y-4">
              <a
                href={'https://dotaudiences.com/cryptoproducts'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuCoins className="w-4 h-4" />
                Crypto Solutions
              </a>
              <a
                href={'https://dotaudiences.com/igamingproducts'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuGamepad2 className="w-4 h-4" />
                iGaming Solutions
              </a>
              <a
                href={'https://dotaudiences.com/creativevault'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.scrollTo(0, 0)}
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuPalette className="w-4 h-4" />
                Creative Vault
              </a>
              <a
                href={'https://dotaudiences.com/support'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuBookOpen className="w-4 h-4" />
                Docs
              </a>
              <a
                href={'https://dotaudiences.com/aboutus'}
                onClick={() => window.scrollTo(0, 0)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuInfo className="w-4 h-4" />
                About Us
              </a>

              <Link
                to={'/login'}
                className="flex items-center gap-1.5 text-[#BFBFBF] hover:text-white transition-colors font-medium"
              >
                <LuLogIn className="w-4 h-4" />
                Log in
              </Link>

              <Button
                onClick={() => navigate('/register')}
                className="w-full bg-white hover:bg-slate-200 text-[#161616] transition-colors font-medium text-sm flex items-center gap-1"
              >
                Register
              </Button>

              <Button
                onClick={openCalendly}
                className="w-full bg-gradient-to-r from-[#9B5CFF] to-[#EC4899] hover:from-[#8B4CFF] hover:to-[#DB2777] text-white"
              >
                Book a Demo
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div className="pt-20">
        <DotAdsLanding />
      </div>

      {/* {!isAdminPage && ( */}
      <div className="chat-modal-scale">
        <LiveChatPrompt key={location.pathname} requiredScrollPercent={0} />
      </div>
      {/* )} */}

      {/* Footer */}
      <footer className="bg-[#0D0D0D] border-t border-white/10 text-[#9E9E9E] mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/690a28bada88e702ec8f793d/572564efd_DOTLogo-Basic-White.png"
                alt="DOT Logo"
                className="h-24 w-auto mb-4"
              />
              <p className="text-sm text-[#10B981] mb-6">We're Hiring!</p>
              <a
                href="mailto:hello@dotaudiences.com"
                className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span className="text-xs">hello@dotaudiences.com</span>
              </a>
            </div>

            {/* Contact Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href={'https://dotaudiences.com/support'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Help & Support
                  </a>
                </li>
                <li>
                  <a
                    href="https://t.me/DotAds_Bot?start=64d0f2cf2f590d0625058864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Telegram Us
                  </a>
                </li>
                <li>
                  <button onClick={openCalendly} className="hover:text-white transition-colors">
                    Book Demo
                  </button>
                </li>
              </ul>
            </div>

            {/* Crypto Solutions Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">Crypto Solutions</h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={'/'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    DOT Ads
                  </Link>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/metaads'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Meta Ads
                  </a>
                </li>
                <li>
                  <a
                    href="https://newswire.dotaudiences.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    PR Newswire
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/creativepricing'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Creative Design
                  </a>
                </li>
              </ul>
            </div>

            {/* iGaming Solutions Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">iGaming Solutions</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href={'/'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    DOT Ads
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/metaadsigaming'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Meta Ads
                  </a>
                </li>
                <li>
                  <a
                    href="https://newswire.dotaudiences.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    PR Newswire
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/creativepricing'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Creative Design
                  </a>
                </li>
              </ul>
            </div>

            {/* Free Resources Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">Free Resources</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://blog.dotaudiences.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Growth Hacks
                  </a>
                </li>
                <li>
                  <a
                    href="https://blog.dotaudiences.com/t/case-studies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Case Studies
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/creativevault'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Creative Vault
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href={'https://dotaudiences.com/aboutus'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/agenciesaffiliates'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Agencies & Affiliates
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/advertiser-terms-of-service'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/advertiser-guidelines'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Advertiser Guidelines
                  </a>
                </li>
                <li>
                  <a
                    href={'https://dotaudiences.com/privacy-policy'}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-[#9E9E9E] hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-sm text-center">
            <p>© 2025 DOT All Rights Reserved</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
